import { MultiSelect, NumberInput, Select, Textarea, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useTranslation } from "react-i18next";

import type { TransitionField, TransitionFieldValue } from "@msc/shared";

import { toLocalDateString } from "../../lib/date.js";

/**
 * Die Feldmaske aus Jira — gezeichnet für die Statusübergänge (#335) und das
 * Anlegen eines Tickets (#271). Beide liefern dieselben Feldarten.
 */

/** Eingabewert eines Maskenfelds in der Oberfläche; Datumsfelder halten ein `Date`. */
export type Eingabe = TransitionFieldValue | Date | undefined;

export const istLeer = (v: Eingabe) =>
  v === undefined || v === null || (typeof v === "string" && v.trim() === "") || (Array.isArray(v) && v.length === 0);

/**
 * Was an die API geht: nur gefüllte Felder. Ein leeres Datum geht nur dann als
 * `null` mit, wenn es vorbelegt war und bewusst geleert wurde — leer ohne
 * Vorbelegung heißt „unverändert lassen", nicht „löschen" (C2).
 */
export function maskenwerte(
  felder: TransitionField[],
  werte: Record<string, Eingabe>,
  vorbelegt: Record<string, Eingabe>,
): Record<string, TransitionFieldValue> {
  const out: Record<string, TransitionFieldValue> = {};
  for (const f of felder) {
    const v = werte[f.key];
    if (istLeer(v)) {
      if (f.kind === "date" && !istLeer(vorbelegt[f.key])) out[f.key] = null;
      continue;
    }
    out[f.key] = v instanceof Date ? toLocalDateString(v) : (v as TransitionFieldValue);
  }
  return out;
}

/** Ein Feld der Maske, gezeichnet nach seiner Art; Beschriftung ist Jiras Feldname. */
export function MaskenFeld({ feld, wert, onChange }: { feld: TransitionField; wert: Eingabe; onChange: (v: Eingabe) => void }) {
  const { t } = useTranslation();
  const auswahl = (feld.allowedValues ?? []).map((v) => ({ value: v.id, label: v.name }));
  const gemeinsam = { label: feld.name, required: feld.required };
  switch (feld.kind) {
    case "select":
      return (
        <Select
          {...gemeinsam}
          placeholder={t("pages.customerView.cockpit.actions.fieldPick")}
          data={auswahl}
          clearable={!feld.required}
          value={typeof wert === "string" ? wert : null}
          onChange={(v) => onChange(v)}
        />
      );
    case "multiselect":
      return <MultiSelect {...gemeinsam} data={auswahl} value={Array.isArray(wert) ? wert : []} onChange={onChange} />;
    case "date":
      return (
        <DateInput
          {...gemeinsam}
          value={wert instanceof Date ? wert : null}
          onChange={(v) => onChange(v)}
          clearable
          clearButtonProps={{ "aria-label": t("pages.customerView.cockpit.actions.fieldClear", { field: feld.name }) }}
        />
      );
    case "number":
      return (
        <NumberInput
          {...gemeinsam}
          value={typeof wert === "number" ? wert : ""}
          onChange={(v) => onChange(typeof v === "number" ? v : null)}
        />
      );
    case "text":
      return <TextInput {...gemeinsam} value={typeof wert === "string" ? wert : ""} onChange={(e) => onChange(e.currentTarget.value)} />;
    case "textarea":
      return (
        <Textarea
          {...gemeinsam}
          autosize
          minRows={2}
          value={typeof wert === "string" ? wert : ""}
          onChange={(e) => onChange(e.currentTarget.value)}
        />
      );
  }
}
